import React from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { ThemeProvider } from './theme';
import { BrandProvider } from './contexts/BrandContext';
import { AuthProvider, useAuth } from './pages/Auth';
import { ToastProvider } from './contexts/ToastContextProvider';
import { ConsentProvider } from './contexts/ConsentContextProvider';
import { LanguageProvider } from './contexts/LanguageContext.tsx';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import './i18n';
import { MainLayout, ProtectedRoute } from './components/layout';
import { DesignSystem } from './pages/DesignSystem';
import { Dashboard } from './pages/Dashboard';
import { Agents } from './pages/Agents/Agents';
import { CreateAgent } from './pages/Agents/CreateAgent';
import { EditAgent } from './pages/Agents/EditAgent';
import { Calls } from './pages/Calls/Calls';
import { Users } from './pages/Users/Users';
import { Analytics } from './pages/Analytics/Analytics';
import { Billing } from './pages/Billing/Billing';
import { CallRouting } from './pages/CallRouting/CallRouting';
import { PhoneNumbers } from './pages/PhoneNumbers/PhoneNumbers';
import { Calendars } from './pages/Calendars/Calendars';
import { CalendarOAuthCallback } from './pages/Calendars/CalendarOAuthCallback';
import { CalendarConnectSuccess } from './pages/Calendars/CalendarConnectSuccess';
import { SchedulingCalendarDetail } from './pages/Calendars/scheduling/SchedulingCalendarDetail';
import { Appointments } from './pages/Appointments';
import { KnowledgeBase } from './pages/KnowledgeBase/KnowledgeBase';
import { Communications } from './pages/Communications/Communications';
import { ProfileSettings } from './pages/ProfileSettings/ProfileSettings';
import { Organization } from './pages/Organization/Organization';
import { BlockedNumbers } from './pages/BlockedNumbers';
import { Login, Register, PasswordReset, PasswordResetConfirm, SignOut } from './pages/Auth';
import { ManageBooking, BookingPage } from './pages/Booking';

const RESELLER_BLOCKED_PATHS = new Set([
  '/billing',
  '/users',
  '/calendars',
  '/appointments',
  '/knowledge-base',
  '/communications',
  '/calls',
  '/organization',
]);

const ResellerBlockedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { pathname } = useLocation();
  const orgRole =
    typeof window !== 'undefined' ? localStorage.getItem('organization_role') || '' : '';
  if (orgRole === 'reseller' && RESELLER_BLOCKED_PATHS.has(pathname)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const getActiveItem = () => {
    const path = location.pathname;
    if (path === '/') return 'dashboard';
    else if (path === '/design-system') return 'design-system';
    else if (path === '/dashboard') return 'dashboard';
    else if (path === '/agents' || path.startsWith('/agents/')) return 'agent-management';
    else if (path === '/calls') return 'calls';
    else if (path === '/users') return 'users';
    else if (path === '/analytics') return 'analytics';
    else if (path === '/billing') return 'billing';
    else if (path === '/call-routing') return 'call-routing';
    else if (path === '/phone-numbers') return 'phone-numbers';
    else if (path === '/blocked-numbers') return 'blocked-numbers';
    else if (path === '/calendars' || path.startsWith('/calendars/')) return 'calendars';
    else if (path === '/appointments' || path.startsWith('/appointments/')) return 'appointments';
    else if (path === '/knowledge-base') return 'knowledge-base';
    else if (path === '/communications') return 'communications';
    return 'dashboard';
  };

  const handleProfileAction = (action: string) => {
    if (action === 'profile') {
      navigate('/profile-settings');
    } else if (action === 'organization') {
      navigate('/organization');
    }
  };

  return (
    <Routes>
      {/* Standalone pages (no layout) */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<PasswordReset />} />
      <Route path="/password/reset/confirm/" element={<PasswordResetConfirm />} />
      <Route path="/sign-out" element={<SignOut />} />
      <Route path="/calendars/oauth/callback" element={<CalendarOAuthCallback />} />
      <Route path="/calendars/connect/success" element={<CalendarConnectSuccess />} />
      <Route path="/manage/:token" element={<ManageBooking />} />
      <Route path="/book/:slug" element={<BookingPage />} />

      {/* Pages with MainLayout - Protected Routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <MainLayout
              user={
                user
                  ? {
                      name: user.first_name + ' ' + user.last_name,
                      email: user.email,
                      role: user.role || 'User',
                      avatar: undefined, // AuthUser doesn't have avatar property
                    }
                  : {
                      name: 'John Doe',
                      email: 'john.doe@acmecorp.com',
                      role: 'Organization Admin',
                    }
              }
              isAuthenticated={isAuthenticated}
              activeItem={getActiveItem()}
              onProfileAction={handleProfileAction}
            >
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/design-system" element={<DesignSystem />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/agents" element={<Agents />} />
                <Route path="/agents/create" element={<CreateAgent />} />
                <Route path="/agents/edit/:id" element={<EditAgent />} />
                <Route
                  path="/calls"
                  element={
                    <ResellerBlockedRoute>
                      <Calls />
                    </ResellerBlockedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ResellerBlockedRoute>
                      <Users />
                    </ResellerBlockedRoute>
                  }
                />
                <Route path="/analytics" element={<Analytics />} />
                <Route
                  path="/billing"
                  element={
                    <ResellerBlockedRoute>
                      <Billing />
                    </ResellerBlockedRoute>
                  }
                />
                <Route path="/call-routing" element={<CallRouting />} />
                <Route path="/phone-numbers" element={<PhoneNumbers />} />
                <Route path="/blocked-numbers" element={<BlockedNumbers />} />
                <Route
                  path="/calendars"
                  element={
                    <ResellerBlockedRoute>
                      <Calendars />
                    </ResellerBlockedRoute>
                  }
                />
                <Route
                  path="/calendars/:id"
                  element={
                    <ResellerBlockedRoute>
                      <SchedulingCalendarDetail />
                    </ResellerBlockedRoute>
                  }
                />
                <Route
                  path="/appointments/:profileId?"
                  element={
                    <ResellerBlockedRoute>
                      <Appointments />
                    </ResellerBlockedRoute>
                  }
                />
                <Route
                  path="/knowledge-base"
                  element={
                    <ResellerBlockedRoute>
                      <KnowledgeBase />
                    </ResellerBlockedRoute>
                  }
                />
                <Route
                  path="/communications"
                  element={
                    <ResellerBlockedRoute>
                      <Communications />
                    </ResellerBlockedRoute>
                  }
                />
                <Route path="/profile-settings" element={<ProfileSettings />} />
                <Route
                  path="/organization"
                  element={
                    <ResellerBlockedRoute>
                      <Organization />
                    </ResellerBlockedRoute>
                  }
                />
              </Routes>
            </MainLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider defaultTheme="system">
      <BrandProvider>
        <LanguageProvider>
          <AuthProvider>
            <ConsentProvider>
              <ToastProvider>
                <AppContent />
                <CookieConsentBanner />
              </ToastProvider>
            </ConsentProvider>
          </AuthProvider>
        </LanguageProvider>
      </BrandProvider>
    </ThemeProvider>
  );
};

export default App;
